import { useMutation, useQueryClient } from '@tanstack/react-query';

import type { ObjectResponse } from '@verantos-dev/types-common';

import { authFetch } from './fetchers';
import { avKeys } from './queryKeys';
import type { AnalyticalVariableDetail } from './useAnalyticalVariableDetailQuery';

export type AvScopeTarget = 'project' | 'organization';

export interface UpdateAvScopeMutationInput {
  analyticalVariableId: string;
  scope: AvScopeTarget;
}

interface MutationContext {
  /**
   * Snapshot of the libraryDetail cache entry before we optimistically
   * overwrote it. Used by ``onError`` to restore the cache when the
   * server rejects the mutation. ``undefined`` when there was no prior
   * cache entry (e.g., user opened the detail surface for the first
   * time and the optimistic write went into an empty cache slot — we
   * delete the slot on rollback rather than restore to undefined).
   */
  previousDetail: ObjectResponse<AnalyticalVariableDetail> | undefined;
}

/**
 * Mutation hook for promote / demote of an analytical variable's scope.
 *
 * Calls ``PATCH /analytical-variables/{id}`` with ``{ scope }``. Server
 * enforces the lock check on demotion (org→project only allowed when no
 * other consumers exist); on refusal returns 409 with structured
 * ``av_locked`` detail. The 409-rendering / lock-reason surfacing
 * happens in ``AvScopeControl``; this hook is only responsible for the
 * wire call and cache state.
 *
 * Optimistic-with-rollback cache strategy:
 *   - ``onMutate`` writes the target scope into ``avKeys.libraryDetail(id)``
 *     immediately, so the button flips loading → opposite-action with
 *     no flicker through the prior state. Snapshot of the prior value
 *     is returned as the mutation's context for rollback.
 *   - ``onError`` restores the snapshot (or deletes the slot if there
 *     was no prior entry), reverting the UI to the pre-click state so
 *     the user can see their click had no effect.
 *   - ``onSuccess`` overwrites with the server's authoritative response.
 *   - ``onSettled`` invalidates every analytical-variable query — list
 *     pages (library list, cohort-builder "Add variable" picker),
 *     details, profiles, cohort-AV lists — so anywhere this variable
 *     is rendered picks up the new scope without a manual refresh.
 *     Invalidation is keyed on ``avKeys.all`` (the root prefix) so the
 *     match is exhaustive — partial keys like ``library()`` would
 *     leave cohort-builder lists stale.
 */
export const useUpdateAvScopeMutation = () => {
  const queryClient = useQueryClient();
  return useMutation<
    ObjectResponse<AnalyticalVariableDetail>,
    unknown,
    UpdateAvScopeMutationInput,
    MutationContext
  >({
    mutationFn: ({ analyticalVariableId, scope }) =>
      authFetch<ObjectResponse<AnalyticalVariableDetail>>(
        `/analytical-variables/${analyticalVariableId}`,
        {
          method: 'PATCH',
          body: JSON.stringify({ scope }),
        }
      ),
    onMutate: async ({ analyticalVariableId, scope }) => {
      // Cancel any in-flight refetches so a delayed-response GET
      // doesn't overwrite the optimistic value with the pre-change
      // server state.
      await queryClient.cancelQueries({
        queryKey: avKeys.libraryDetail(analyticalVariableId),
      });
      const previousDetail = queryClient.getQueryData<ObjectResponse<AnalyticalVariableDetail>>(
        avKeys.libraryDetail(analyticalVariableId)
      );
      if (previousDetail?.data) {
        // Shallow-merge: only the scope field changes. Everything else
        // (name, sqlBody, manifest, etc.) stays pinned so the UI doesn't
        // visually shift on the optimistic write.
        queryClient.setQueryData<ObjectResponse<AnalyticalVariableDetail>>(
          avKeys.libraryDetail(analyticalVariableId),
          {
            ...previousDetail,
            data: { ...previousDetail.data, scope },
          }
        );
      }
      return { previousDetail };
    },
    onError: (_err, { analyticalVariableId }, context) => {
      // Restore the pre-mutation snapshot so the button reverts to the
      // user's pre-click state. They see the error surfaced separately
      // by AvScopeControl; the chip / button shouldn't be wrong.
      if (context?.previousDetail !== undefined) {
        queryClient.setQueryData(
          avKeys.libraryDetail(analyticalVariableId),
          context.previousDetail
        );
      } else {
        // No prior snapshot — drop the optimistic write entirely.
        queryClient.removeQueries({
          queryKey: avKeys.libraryDetail(analyticalVariableId),
        });
      }
    },
    onSuccess: (data, { analyticalVariableId }) => {
      // Overwrite with the server's authoritative response (covers any
      // server-side normalization, e.g., scope lowercasing or status
      // transitions). Matches the optimistic value in the happy case.
      if (data?.data) {
        queryClient.setQueryData(avKeys.libraryDetail(analyticalVariableId), data);
      }
    },
    onSettled: () => {
      // Invalidate every analytical-variable query so the new scope
      // propagates to list surfaces (library page, cohort-builder
      // "Add variable" picker, project page artifact list) and other
      // detail views. ``avKeys.all`` is the root prefix; TanStack
      // does prefix matching, so this catches lists / details /
      // profiles / library entries in one call.
      void queryClient.invalidateQueries({ queryKey: avKeys.all });
    },
  });
};
